import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { MB_PALETTE, LEVEL_STYLES, MATURITY_LEVELS } from "@/components/mai/constants";
import { DossierWorkspace } from "@/components/mai/DossierWorkspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIER_CHIP = {
  global_core: "bg-emerald-600 text-white",
  adaptable: "bg-amber-500 text-white",
  local_only: "bg-slate-200 text-slate-600",
};
const TIER_LABEL = { global_core: "Global Core", adaptable: "Adaptable", local_only: "Local Only" };
const SECTION_STATUS = {
  filled: { label: "Filled", cls: "bg-emerald-700 text-white" },
  partially_filled: { label: "Partially filled", cls: "bg-amber-600 text-white" },
  blank: { label: "Blank", cls: "bg-red-700 text-white" },
  local_authoring: { label: "Local authoring", cls: "bg-blue-700 text-white" },
};
const ACCEPT_CHIP = {
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  adaptation: "bg-amber-50 text-amber-700 border-amber-200",
  not_acceptable: "bg-red-50 text-red-600 border-red-200",
  not_required: "bg-slate-50 text-slate-400 border-slate-200",
  platform: "bg-slate-100 text-slate-600 border-slate-200",
};

function BucketView({ d }) {
  return (
    <div className="space-y-8">
      <div data-testid="dossier-summary" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 border border-slate-200 bg-white divide-x divide-y md:divide-y-0 divide-slate-200">
        {[["Global Core Modules", d.summary.global_core_modules], ["Portfolio Assets", d.summary.total_assets],
          ["Bucket Modules", d.summary.bucket_module_count], ["Markets", d.summary.markets],
          ["Avg Annexure Burden", d.summary.avg_annexure_burden], ["Authored-Once Ratio", `${d.summary.authored_once_ratio}%`]].map(([l, v]) => (
          <div key={l} className="p-5"><p className="overline-label text-slate-400">{l}</p><p className="font-heading font-extrabold text-2xl mt-2">{v}</p></div>
        ))}
      </div>

      <section data-testid="dossier-core">
        <p className="overline-label text-slate-400">Tier 1 · Authored Once</p>
        <h2 className="font-heading font-bold text-lg tracking-tight mt-1 mb-4">Global Core Dossier</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {d.global_core.map((m) => (
            <div key={m.asset_id} data-testid={`core-module-${m.asset_id}`} className="bg-white border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900 leading-snug">{m.asset_name}</p>
                <span className={`shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${TIER_CHIP[m.tier]}`}>{TIER_LABEL[m.tier]}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{m.asset_type} · {MATURITY_LEVELS[m.maturity] || m.maturity}</p>
              <div className="mt-3 h-1.5 bg-slate-100"><div className="h-full bg-emerald-700" style={{ width: `${m.core_coverage_pct}%` }} /></div>
              <p className="font-mono-data text-[10px] text-slate-400 mt-1.5">
                as-is in {m.accepted_as_is.length} markets ({m.core_coverage_pct}%) · adaptation in {m.adaptation_markets.length}
              </p>
              <p className="text-[11px] text-slate-500 mt-2">Covers: {m.covers_requirements.join(", ")}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="dossier-buckets">
        <p className="overline-label text-slate-400">Tier 2 · Parameterized per Bucket</p>
        <h2 className="font-heading font-bold text-lg tracking-tight mt-1 mb-4">Bucket Modules</h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {d.bucket_modules.filter((b) => b.modules.length > 0).map((b, i) => (
            <div key={b.bucket_id} data-testid={`bucket-module-${b.bucket_id}`} className="bg-white border border-slate-200" style={{ borderTop: `3px solid ${MB_PALETTE[i % MB_PALETTE.length]}` }}>
              <div className="px-5 py-3.5 border-b border-slate-100">
                <p className="font-mono-data text-[10px] text-slate-400">{b.bucket_id}</p>
                <p className="text-sm font-bold text-slate-900">{b.name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{b.members.join(" · ")}</p>
              </div>
              <div className="divide-y divide-slate-100">
                {b.modules.map((m, j) => (
                  <div key={j} className="px-5 py-3">
                    <p className="text-xs font-semibold text-slate-800">
                      {m.module}
                      <span className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${m.kind === "generation" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>{m.kind}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{m.parameterization}</p>
                    <p className="font-mono-data text-[10px] text-slate-400 mt-1">{m.markets.join(", ")}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="dossier-annexures">
        <p className="overline-label text-slate-400">Tier 3 · Irreducibly Local</p>
        <h2 className="font-heading font-bold text-lg tracking-tight mt-1 mb-4">Country Annexures</h2>
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-200 text-left">
              {["Market", "Dossier Language", "Core Coverage", "Annexure Items", "Burden"].map((h) => <th key={h} className="px-5 py-3 overline-label text-slate-400 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {d.country_annexures.map((a) => (
                <tr key={a.id} data-testid={`annexure-row-${a.id}`}>
                  <td className="px-5 py-3 font-semibold whitespace-nowrap align-top">{a.flag} {a.name}</td>
                  <td className="px-5 py-3 align-top">{a.dossier_language}{a.translation_required && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-900 text-white">Translate</span>}</td>
                  <td className="px-5 py-3 align-top">
                    <div className="w-20 h-1.5 bg-slate-100"><div className="h-full bg-emerald-700" style={{ width: `${a.coverage_rate}%` }} /></div>
                    <p className="font-mono-data text-[10px] text-slate-400 mt-1">{a.coverage_rate}%</p>
                  </td>
                  <td className="px-5 py-3 align-top">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {a.annexure_items.map((it, j) => (
                        <span key={j} className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${LEVEL_STYLES[it.level] || "bg-slate-100 text-slate-500"}`} title={it.action}>
                          {it.requirement_type} · {it.status}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-heading font-extrabold text-base align-top">{a.annexure_burden}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function TemplateView({ productId }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setData(null); setSelected(null);
    axios.get(`${API}/hta-templates/${productId}/view`).then((r) => {
      setData(r.data);
      if (r.data.views.length > 0) setSelected(r.data.views[0].template_id);
    }).catch(console.error);
  }, [productId]);

  if (!data) return <p className="py-16 text-center text-xs text-slate-400">Mapping dossier content onto HTA templates…</p>;
  const v = data.views.find((x) => x.template_id === selected);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {data.views.map((x) => (
          <button key={x.template_id} data-testid={`template-select-${x.template_id}`} onClick={() => setSelected(x.template_id)}
            className={`text-xs font-semibold px-3.5 py-2 border transition-colors ${selected === x.template_id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            {x.market.flag} {x.market.name} · {x.fill_pct}%
          </button>
        ))}
      </div>

      {v && (
        <div data-testid="template-view-panel" className="bg-white border border-slate-200">
          <div className="px-6 py-5 border-b border-slate-200 flex flex-wrap items-center gap-x-8 gap-y-3">
            <div>
              <p className="overline-label text-slate-400">{v.hta_body} · {v.market.name} · Dossier in {v.dossier_language}</p>
              <p className="font-heading font-bold text-base tracking-tight mt-1">{v.name}</p>
              <p className="text-[11px] text-slate-500 mt-0.5 max-w-2xl">{v.format_note}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="font-heading font-extrabold text-3xl">{v.fill_pct}<span className="text-sm text-slate-400">%</span></p>
              <p className="text-[10px] text-slate-400">sections fillable from platform content</p>
            </div>
          </div>
          <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-4 text-[11px]">
            <span className="text-emerald-700 font-semibold">{v.counts.filled} filled</span>
            <span className="text-amber-600 font-semibold">{v.counts.partially_filled} partially filled</span>
            <span className="text-red-600 font-semibold">{v.counts.blank} blank</span>
            <span className="text-blue-700 font-semibold">{v.counts.local_authoring} local authoring</span>
          </div>
          <div className="divide-y divide-slate-100">
            {v.sections.map((s) => {
              const m = SECTION_STATUS[s.status];
              return (
                <div key={s.order} data-testid={`template-section-${v.template_id}-${s.order}`} className="px-6 py-3.5 flex items-start gap-4">
                  <span className="font-mono-data text-[10px] text-slate-300 mt-1 shrink-0 w-5">{String(s.order).padStart(2, "0")}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800">{s.name}
                      <span className={`ml-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${m.cls}`}>{m.label}</span>
                    </p>
                    {s.filled_by.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {s.filled_by.map((f, i) => f.kind === "asset" ? (
                          <Link key={i} to="/console/inventory" title={f.rationale}
                            className={`text-[10px] px-2 py-0.5 border hover:underline ${ACCEPT_CHIP[f.acceptance] || ACCEPT_CHIP.not_required}`}>
                            {f.name} · {f.acceptance.replace("_", " ")}
                          </Link>
                        ) : (
                          <span key={i} className={`text-[10px] px-2 py-0.5 border ${ACCEPT_CHIP.platform}`}>{f.name}</span>
                        ))}
                      </div>
                    )}
                    {s.status === "blank" && <p className="text-[10px] text-slate-400 mt-1">No platform evidence maps to this section — generate or ingest matching evidence.</p>}
                    {s.status === "local_authoring" && <p className="text-[10px] text-slate-400 mt-1">Administrative / market-specific content authored by the local team.</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.unmatched.length > 0 && (
        <div data-testid="template-unmatched" className="bg-white border border-dashed border-slate-300 px-6 py-4">
          <p className="overline-label text-slate-400">Templates without an active target market</p>
          {data.unmatched.map((u) => (
            <p key={u.id} className="text-[11px] text-slate-500 mt-1.5"><span className="font-semibold text-slate-700">{u.name}</span> ({u.market}, {u.hta_body}) — {u.note}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateLibrary() {
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hta-templates`).then((r) => setTemplates(r.data)).catch(console.error);
  }, []);

  if (!templates) return <p className="py-16 text-center text-xs text-slate-400">Loading template library…</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500 max-w-3xl leading-relaxed">
        Named section structures required by each HTA authority, stored as editable configuration data — new templates can be added as agency guidance changes, without a code change. Read-only view.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="overline-label text-slate-400">{t.hta_body} · {t.market}</p>
              <p className="font-heading font-bold text-sm tracking-tight mt-1">{t.name}</p>
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{t.format_note}</p>
            </div>
            <ol className="px-5 py-3 space-y-1.5">
              {[...t.sections].sort((a, b) => a.order - b.order).map((s) => (
                <li key={s.order} className="text-xs text-slate-700 flex gap-3">
                  <span className="font-mono-data text-[10px] text-slate-300 w-5 shrink-0">{String(s.order).padStart(2, "0")}</span>
                  {s.name}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DossierPage() {
  const { productId } = useProduct();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("workspace");

  useEffect(() => {
    setD(null);
    axios.get(`${API}/dossier-architecture/${productId}`).then((r) => setD(r.data)).catch(console.error);
  }, [productId]);

  return (
    <PageShell pageId="dossier" overline="Modular Dossier Engine · Build Once, Deploy Many" title="Submission Dossier Builder">
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["workspace", "Sections"], ["bucket", "Bucket View"], ["template", "Template View"], ["library", "Template Library"]].map(([id, label]) => (
          <button key={id} data-testid={`dossier-tab-${id}`} onClick={() => setTab(id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "workspace" && <DossierWorkspace />}
      {tab === "bucket" && (!d ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Assembling dossier architecture…</div>
      ) : <BucketView d={d} />)}
      {tab === "template" && <TemplateView productId={productId} />}
      {tab === "library" && <TemplateLibrary />}
    </PageShell>
  );
}
